import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScoreTypeClient, ScoreTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ScoreTypeClient(API_BASE_URL, axiosInstance);

// Cached hook for all active score types (used in dropdowns/lookups)
export const useScoreTypes = () => {
  return useQuery({
    queryKey: ['scoreTypes', 'all'],
    queryFn: async () => {
      const response = await apiClient.searchScoreTypes({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search hook for admin pages with filters
export const useScoreTypeSearch = () => {
  const search = async (params: any) => {
    const response = await apiClient.searchScoreTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get single score type by ID
export const useScoreTypeById = (id?: string) => {
  return useQuery({
    queryKey: ['scoreTypes', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await apiClient.getScoreTypeById(id);
      return response?.data || null;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateScoreType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ScoreTypeDTO) => {
      return await apiClient.createScoreType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoreTypes'] });
    },
  });
};

// Update mutation
export const useUpdateScoreType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ScoreTypeDTO }) => {
      return await apiClient.updateScoreType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoreTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteScoreType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteScoreType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoreTypes'] });
    },
  });
};